import { COUNTRIES } from '../../config/countries-data'
import {
  type Address,
  type AddressFormatThatShouldntExist,
  type NormalizedAddress,
} from './address.types'
import { translateManager as t } from '../../services/TranslateManager'
import { ADDRESS_VALIDATION_TIMEOUT_MS, ERROR_CODES } from './address.consts'
import { mixtilesAxios } from '../../utils/ApiUtils'
import { analytics } from '../../services/Analytics/Analytics'
import { MATERIAL_TYPES } from '@mixtiles/web-backend-shared'
import { getTileMaterialType } from '../../services/tileMaterial'
import { type Tile } from '../../components/TilesProvider/TilesProvider.types'

export function normalizeAddress(
  address: Address | null,
  addressValidationSkipped: boolean
): NormalizedAddress | null {
  if (!(address && address.countryId.toUpperCase() in COUNTRIES)) {
    return null
  }

  return {
    fullName: address.fullName,
    fullNameKana: address.fullNameKana,
    streetAddress: address.street,
    streetAddress2: address.address_2,
    city: address.city,
    state: address.state || '',
    zip: address.zipCode,
    country: COUNTRIES[address.countryId].code,
    phoneNumber: address.phoneNumber,
    shouldValidate: !addressValidationSkipped,
  }
}

// Converts to the local format.
// This function is the opposite of normalize address
export function denormalizeAddress(address: NormalizedAddress) {
  if (!address || !Object.keys(address).length) {
    return null
  }

  return {
    fullName: address.fullName,
    fullNameKana: address.fullNameKana,
    street: address.streetAddress,
    address_2: address.streetAddress2,
    city: address.city,
    state: address.state || '',
    zipCode: address.zip,
    countryId: (
      Object.values(COUNTRIES).find(({ code }) => code === address.country) ||
      {}
    ).id,
    phoneNumber: address.phoneNumber,
  }
}

const LATIN_FULL_NAME_PATTERN = /[a-zA-Z]+\s+[a-zA-Z]+/
const JAPANESE_CHARACTERS_PATTERN =
  /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/
const KANJI_PATTERN = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/
const KANA_ONLY_PATTERN = /^[\u3040-\u309f\u30a0-\u30ff\s]+$/

export function validateFullName(
  fullName: string,
  { allowJapanese = false }: { allowJapanese?: boolean } = {}
): boolean {
  const trimmedFullName = fullName.trim()
  if (allowJapanese && JAPANESE_CHARACTERS_PATTERN.test(trimmedFullName)) {
    return true
  }
  return LATIN_FULL_NAME_PATTERN.test(trimmedFullName)
}

// Kanji names need a separate furigana reading so the backend can romanize them for the
// UPS label. Pure kana names already are their own reading.
export function nameNeedsKanaReading(fullName: string): boolean {
  return KANJI_PATTERN.test(fullName.trim())
}

export function validateFullNameKana(kana: string): boolean {
  const trimmed = kana.trim()
  return trimmed.length > 0 && KANA_ONLY_PATTERN.test(trimmed)
}

export type NameKanaError = 'missing' | 'invalid'

export const NAME_KANA_ERROR_MESSAGE_KEYS: Record<NameKanaError, string> = {
  missing: 'general.address_form.validation_errors.full_name_kana.missing',
  invalid: 'general.address_form.validation_errors.full_name_kana.invalid',
}

// Single source of truth for the furigana requirement, shared by every checkout path that can
// produce a shipping address (the dedicated AddressForm and the billing-as-shipping payment flow).
export function getNameKanaError(
  fullName: string | undefined,
  fullNameKana: string | undefined
): NameKanaError | null {
  const kana = fullNameKana?.trim()
  if (fullName && nameNeedsKanaReading(fullName) && !kana) {
    return 'missing'
  }
  if (kana && !validateFullNameKana(kana)) {
    return 'invalid'
  }
  return null
}

const NON_LATIN_PATTERN = /[\u0590-\u05FF\u0370-\u03FF]/
const ADDRESS_TEXT_FIELDS = ['fullName', 'street', 'address_2', 'city'] as const

export function validateAddressLatinCharacters(address: Address): boolean {
  return !ADDRESS_TEXT_FIELDS.some(
    (field) => address[field] && NON_LATIN_PATTERN.test(address[field])
  )
}

export function shouldCapturePhone({
  countryId,
  tiles,
  phoneNumber,
}: {
  countryId: string
  tiles: Tile[]
  phoneNumber?: string
}): boolean {
  const country = COUNTRIES[countryId]
  return (
    tiles.some((tile) =>
      [MATERIAL_TYPES.CANVAS].includes(getTileMaterialType(tile))
    ) ||
    country.code !== 'US' ||
    !!phoneNumber
  )
}

export function validateAddress(address: Address) {
  const {
    fullName,
    countryId,
    street,
    address_2: address2,
    city,
    state,
    zipCode,
  } = address

  const addressValues = {
    name: fullName,
    country: countryId,
    street1: street,
    street2: address2,
    city,
    state: state || '',
    zip: zipCode,
  }
  return callValidateAddress(addressValues)
}

export function isPOBoxAddress(street: string): boolean {
  return (
    !!street.match(/\bp\.?o\.?\s*box\b/i) || !!street.match(/\bp\.?o\.?b\.?\b/i)
  )
}

export function isPOBoxAddressOutsideUS(
  countryId: string,
  street: string
): boolean {
  if (!countryId || countryId.toUpperCase() === 'UNITED_STATES' || !street) {
    return false
  }

  return isPOBoxAddress(street)
}

function callValidateAddress(address: AddressFormatThatShouldntExist) {
  const data = {
    ...address,
    requiresOldFreeTiles: true,
  }

  return mixtilesAxios
    .post('v1/address/validate', data, {
      timeout: ADDRESS_VALIDATION_TIMEOUT_MS,
    })
    .then((response) => {
      const { isValid, validationErrors } = response.data
      if (isValid) {
        return { isValid: true, allowEditAddress: true }
      }

      analytics.track('Address Validation Failed', {
        Error: JSON.stringify(validationErrors),
      })
      const validationErrorMessages = validationErrors
        .map((error: any) => getMessageForEpostValidationError(error.code))
        .filter((message: string) => message !== '')
      if (validationErrorMessages.length === 0) {
        validationErrorMessages.push(
          t.get('general.address_form.validation_errors.address.invalid')
        )
      }
      return {
        isValid,
        allowEditAddress: response.data.allowEditAddress !== false,
        validationErrors: validationErrorMessages,
      }
    })
}

export function getMessageForEpostValidationError(
  errorCode: (typeof ERROR_CODES)[keyof typeof ERROR_CODES]
) {
  switch (errorCode) {
    case ERROR_CODES.countryUnsupported:
      return t.get('general.address_form.validation_errors.country.unsupported')
    case ERROR_CODES.addressInvalid:
      return t.get('general.address_form.validation_errors.address.invalid')
    case ERROR_CODES.addressNotFound:
      return t.get('general.address_form.validation_errors.address.not_found')
    case ERROR_CODES.secondaryInformationInvalid:
      return t.get(
        'general.address_form.validation_errors.secondary_information.invalid'
      )
    case ERROR_CODES.secondaryInformationMissing:
      return t.get(
        'general.address_form.validation_errors.secondary_information.missing'
      )
    case ERROR_CODES.houseNumberMissing:
      return t.get(
        'general.address_form.validation_errors.house_number.missing'
      )
    case ERROR_CODES.houseNumberInvalid:
      return t.get(
        'general.address_form.validation_errors.house_number.invalid'
      )
    case ERROR_CODES.streetMissing:
      return t.get('general.address_form.validation_errors.street.missing')
    case ERROR_CODES.streetInvalid:
      return t.get('general.address_form.validation_errors.street.invalid')
    case ERROR_CODES.zipNotFound:
      return t.get('general.address_form.validation_errors.zip.not_found')
    case ERROR_CODES.stateZipMismatch:
      return t.get('general.address_form.validation_errors.zip.state_mismatch')
    case ERROR_CODES.zipInvalid:
      return t.get('general.address_form.validation_errors.zip.invalid')
    case ERROR_CODES.zipUnsupportedTerritory:
      return t.get(
        'general.address_form.validation_errors.zip.unsupported_territory'
      )
    case ERROR_CODES.cityStateInvalid:
      return t.get('general.address_form.validation_errors.city_state.invalid')
    case ERROR_CODES.stateInvalid:
      return t.get('general.address_form.validation_errors.state.invalid')
    case ERROR_CODES.poBoxInternational:
      return t.get('general.address_form.validation_errors.street.po_box')
    default:
      return ''
  }
}
